/**
 * LogDetailsModal Component - Epic 38 - Story 38.1
 *
 * Modal para exibir detalhes completos de um log de auditoria
 * Layout THESYS: lista vertical simples sem ícones
 */

import React from 'react';
import { X } from 'lucide-react';
import { AuditLog } from '../types/auditLog';
import { OperationBadge } from './OperationBadge';
import logService from '../services/logService';

interface LogDetailsModalProps {
  log: AuditLog | null;
  isOpen: boolean;
  onClose: () => void;
}

export const LogDetailsModal: React.FC<LogDetailsModalProps> = ({
  log,
  isOpen,
  onClose,
}) => {
  if (!isOpen || !log) return null;

  // Parse JSON values
  const valoresAnteriores = log.valores_anteriores
    ? (typeof log.valores_anteriores === 'string'
        ? JSON.parse(log.valores_anteriores)
        : log.valores_anteriores)
    : null;

  const valoresNovos = log.valores_novos
    ? (typeof log.valores_novos === 'string'
        ? JSON.parse(log.valores_novos)
        : log.valores_novos)
    : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white dark:bg-gray-800 rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Detalhes do Log
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
            {/* ID */}
            <div className="flex gap-2">
              <span className="font-semibold text-gray-900 dark:text-white">ID:</span>
              <span className="text-gray-900 dark:text-white">{log.id}</span>
            </div>

            {/* Usuário */}
            <div className="flex gap-2">
              <span className="font-semibold text-gray-900 dark:text-white">Usuário:</span>
              <span className="text-gray-900 dark:text-white">
                {log.usuario_nome || 'Sistema'}
                {log.usuario_id && ` (ID: ${log.usuario_id})`}
              </span>
            </div>

            {/* Empresa */}
            {log.empresa && (
              <div className="flex gap-2">
                <span className="font-semibold text-gray-900 dark:text-white">Empresa:</span>
                <span className="text-gray-900 dark:text-white">{log.empresa}</span>
              </div>
            )}

            {/* Operação */}
            <div className="flex gap-2 items-center">
              <span className="font-semibold text-gray-900 dark:text-white">Operação:</span>
              <OperationBadge operation={log.operacao} showIcon={false} />
            </div>

            {/* Entidade */}
            <div className="flex gap-2 items-center">
              <span className="font-semibold text-gray-900 dark:text-white">Entidade:</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {log.entidade}
              </span>
            </div>

            {/* ID da Entidade */}
            <div className="flex gap-2">
              <span className="font-semibold text-gray-900 dark:text-white">ID da Entidade:</span>
              <span className="text-gray-900 dark:text-white font-mono">{log.id_entidade}</span>
            </div>

            {/* IP */}
            {log.ip && (
              <div className="flex gap-2">
                <span className="font-semibold text-gray-900 dark:text-white">IP:</span>
                <span className="text-gray-900 dark:text-white font-mono">{log.ip}</span>
              </div>
            )}

            {/* User Agent */}
            {log.user_agent && (
              <div className="flex gap-2">
                <span className="font-semibold text-gray-900 dark:text-white">User Agent:</span>
                <span className="text-gray-900 dark:text-white text-sm break-all">{log.user_agent}</span>
              </div>
            )}

            {/* Data/Hora */}
            <div className="flex gap-2">
              <span className="font-semibold text-gray-900 dark:text-white">Data/Hora:</span>
              <span className="text-gray-900 dark:text-white">{logService.formatDateTime(log.data_hora)}</span>
            </div>

            {/* Valores Anteriores */}
            {valoresAnteriores && (
              <div className="mt-6">
                <h3 className="text-center font-semibold text-gray-900 dark:text-white mb-3">
                  Valores Anteriores
                </h3>
                <div className="bg-gray-800 dark:bg-gray-900 rounded-lg p-4">
                  <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                    {log.operacao === 'DELETE' ? 'Dados Antes da Exclusão:' : 'Antes da Modificação:'}
                  </div>
                  <pre className="text-xs text-gray-200 dark:text-gray-300 font-mono overflow-x-auto">
                    {JSON.stringify(valoresAnteriores, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Valores Novos - com tratamento especial para UPLOAD */}
            {valoresNovos && (
              <div className="mt-6">
                <h3 className="text-center font-semibold text-gray-900 dark:text-white mb-3">
                  {log.operacao === 'UPLOAD' ? 'Detalhes do Upload' : 'Valores Novos'}
                </h3>
                {log.operacao === 'UPLOAD' ? (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 space-y-3">
                    {/* Tipo de Upload */}
                    {valoresNovos.tipo_upload && (
                      <div className="flex gap-2">
                        <span className="font-semibold text-gray-700 dark:text-gray-300">Tipo:</span>
                        <span className="px-2 py-0.5 bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded text-sm font-medium">
                          {valoresNovos.tipo_upload}
                        </span>
                      </div>
                    )}
                    {/* Arquivo */}
                    {valoresNovos.arquivo_nome && (
                      <div className="flex gap-2">
                        <span className="font-semibold text-gray-700 dark:text-gray-300">Arquivo:</span>
                        <span className="text-gray-900 dark:text-white font-mono text-sm">{valoresNovos.arquivo_nome}</span>
                      </div>
                    )}
                    {/* Entidade */}
                    {valoresNovos.entidade_nome && (
                      <div className="flex gap-2">
                        <span className="font-semibold text-gray-700 dark:text-gray-300">Destino:</span>
                        <span className="text-gray-900 dark:text-white">{valoresNovos.entidade_nome}</span>
                      </div>
                    )}
                    {/* Estatísticas */}
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {valoresNovos.total_linhas ?? 0}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Total Linhas</div>
                      </div>
                      <div className="text-center p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                          {valoresNovos.linhas_processadas ?? 0}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Processadas</div>
                      </div>
                      <div className="text-center p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                        <div className="text-2xl font-bold text-red-700 dark:text-red-400">
                          {valoresNovos.linhas_erro ?? 0}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Erros</div>
                      </div>
                    </div>
                    {/* Tempo de Processamento */}
                    {valoresNovos.tempo_processamento_ms !== undefined && (
                      <div className="flex gap-2 mt-2">
                        <span className="font-semibold text-gray-700 dark:text-gray-300">Tempo:</span>
                        <span className="text-gray-900 dark:text-white">
                          {valoresNovos.tempo_processamento_ms >= 1000
                            ? `${(valoresNovos.tempo_processamento_ms / 1000).toFixed(2)}s`
                            : `${valoresNovos.tempo_processamento_ms}ms`}
                        </span>
                      </div>
                    )}
                    {/* Erros detalhados */}
                    {valoresNovos.erros && valoresNovos.erros.length > 0 && (
                      <div className="mt-4">
                        <div className="font-semibold text-red-700 dark:text-red-400 mb-2">
                          Erros ({valoresNovos.erros.length}):
                        </div>
                        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 max-h-40 overflow-y-auto">
                          {valoresNovos.erros.slice(0, 10).map((err: any, idx: number) => (
                            <div key={idx} className="text-sm text-red-800 dark:text-red-300 mb-1">
                              <span className="font-mono">Linha {err.linha}:</span> {err.erro}
                            </div>
                          ))}
                          {valoresNovos.erros.length > 10 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              ... e mais {valoresNovos.erros.length - 10} erros
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-800 dark:bg-gray-900 rounded-lg p-4">
                    <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                      Após a Modificação:
                    </div>
                    <pre className="text-xs text-gray-200 dark:text-gray-300 font-mono overflow-x-auto">
                      {JSON.stringify(valoresNovos, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
